import React, { useState, useEffect, useCallback } from 'react';
import { Calendar, dateFnsLocalizer } from 'react-big-calendar';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { format, parseISO, startOfMonth, endOfMonth } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../context/AuthContext';
import { MeetingModal } from './modals/MeetingModal';

const localizer = dateFnsLocalizer({
  format,
  parse: (str) => parseISO(str),
  startOfWeek: () => new Date(new Date().setDate(new Date().getDate() - new Date().getDay())),
  getDay: date => date.getDay(),
  locales: { es },
});

export function MeetingsView() {
  const { token } = useAuth();
  const [events, setEvents] = useState([]);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [slotInfo, setSlotInfo] = useState(null);
  const [currentDateRange, setCurrentDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  });

  const fetchEvents = useCallback(async ({ start, end }) => {
    setError('');
    try {
      const response = await fetch(`/api/owner/meetings?start=${start.toISOString()}&end=${end.toISOString()}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (response.ok) {
        const formatted = data.map(e=>({ ...e, start: parseISO(e.start_time), end: parseISO(e.end_time) }));
        setEvents(formatted);
      } else setError(data.mensaje || 'Error al cargar reuniones.');
    } catch { setError('Error de conexión al cargar reuniones.'); }
  }, [token]);

  useEffect(()=>{ fetchEvents(currentDateRange); }, [currentDateRange, fetchEvents]);

  const handleSelectSlot = (slot) => { setSlotInfo(slot); setSelectedEvent(null); setIsModalOpen(true); };
  const handleSelectEvent = (event) => { setSelectedEvent(event); setSlotInfo(null); setIsModalOpen(true); };

  const handleSaveMeeting = () => {
    setIsModalOpen(false); setSelectedEvent(null); setSlotInfo(null);
    fetchEvents(currentDateRange);
  };

  const eventStyleGetter = (event) => {
    const style = {
      backgroundColor: event.color || '#3174ad',
      borderRadius: '5px',
      opacity: 0.8,
      color: 'white',
      border: '0px',
      display: 'block'
    };
    return {
      style: style
    };
  };

  return (
    <div className="rounded-2xl border border-slate-200/70 bg-white/80 p-4 shadow-sm">
      {error && <p className="text-rose-600 mb-3">{error}</p>}
      <div className="h-[70vh] rounded-xl overflow-hidden">
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          views={['month','week','day']}
          style={{ height: '100%' }}
          messages={{ next:"Siguiente", previous:"Anterior", today:"Hoy", month:"Mes", week:"Semana", day:"Día", agenda:"Agenda", date:"Fecha", time:"Hora", event:"Evento" }}
          selectable
          onSelectSlot={handleSelectSlot}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          onRangeChange={(range)=>{
            let start, end;
            if (Array.isArray(range)) {
              start = range[0];
              end = range[range.length - 1];
            } else if (range.start && range.end) {
              start = range.start;
              end = range.end;
            } else {
              start = startOfMonth(range.start || new Date());
              end = endOfMonth(range.end || new Date());
            }
            const r = { start, end }; 
            setCurrentDateRange(r); 
            fetchEvents(r);
          }}
        />
      </div>

      {isModalOpen && (
        <MeetingModal
          event={selectedEvent}
          slotInfo={slotInfo}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveMeeting}
        />
      )}
    </div>
  );
}
